export enum HonorariumLiquidationStatusId {
    OPEN   = 1,
    CLOSED = 2,
}

export enum HonorariumLiquidationProfessionalStatusId {
    PENDING = 1,
    PAID    = 2,
}

export interface CreateLiquidationRequestDTO {
    billingEntityId: number;
    description: string;
    professionalIds: number[];
}

export interface UpdateLiquidationRequestDTO {
    liquidationId: number;
    fromDate: string;
    toDate: string;
    description: string;
}

/**
 * Pago al profesional de una liquidación cerrada, igual que en la rendición: si el sitio maneja cajas,
 * caja y cuenta financiera van juntas (las dos en null si no).
 */
export interface AddPaymentOrderRequestDTO {
    liquidationProfessionalId: number;
    amount: number;
    paymentMethodId: number;
    locationCashRegisterId: number | null;
    financialAccountId: number | null;
}

export interface HonorariumLiquidationPaymentOrderDTO {
    honorariumLiquidationPaymentOrder: number;
    honorariumLiquidationProfessional: number;
    amount: number;
    date: string;
    paymentMethod: number | null;
    paymentMethodName: string;
    administrativeForm: number | null;
    /** false: compensación con el comprobante de gastos de otra liquidación (no sale plata, no tiene medio de pago). */
    paymentOrder: boolean;
}

/** Deducción del profesional en una liquidación: anticipo histórico o deducción de cuenta corriente. */
export interface HonorariumLiquidationPaymentDTO {
    honorariumLiquidationPayment: number;
    honorariumLiquidationProfessional: number;
    amount: number;
    date: string;
    paymentMethod: number | null;
    paymentMethodName: string;
    administrativeForm: number | null;
    /** "Anticipo (histórico)" o "Deducción cuenta corriente". */
    label: string;
}

export interface HonorariumLiquidationDTO {
    honorariumLiquidation: number;
    liquidationNumber: number;
    description: string;
    date: string;
    fromDate: string;
    toDate: string;
    status: string;
    statusId: number;
    /** Honorarios de todos los profesionales. */
    totalHonorarium: number;
    /** Neto a pagar guardado al cerrar; null mientras está abierta. */
    netToPay: number | null;
    billingEntity: number;
    billingEntityName: string;
    site: number;
    professionalCount: number;
    paymentStatus: 'PAID' | 'PENDING' | null;
}

/** Montos de un profesional en la liquidación: todos los calcula el backend (LiquidationAmountsCalculator). */
export interface HonorariumLiquidationProfessionalDTO {
    honorariumLiquidationProfessional: number;
    professional: number;
    professionalName: string;
    professionalType: string;
    patientTotal: number;
    financerTotal: number;
    /** Honorarios. */
    totalAmount: number;
    withholdingsTotal: number;
    advancesTotal: number;
    /** Deducción de cuenta corriente aplicada al cerrar. */
    accountDeduction: number;
    /** Deducciones: anticipos + deducción de cuenta corriente. */
    /** Anticipos + deducciones de cuenta corriente + débitos manuales. */
    deductionsTotal: number;
    /** Débitos manuales (ya incluidos en deductionsTotal). */
    manualDebitsTotal: number;
    /** Deducciones por concepto deducible (ej. Caja de profesionales): ya incluidas en deductionsTotal. */
    conceptDeductionsTotal?: number;
    /** Créditos manuales: se suman a lo que cobra. */
    manualCreditsTotal: number;
    /**
     * Retenciones por facturación propia: lo que se le retiene de las órdenes que facturó él. Positivo se descuenta
     * como los gastos; negativo (anulaciones de órdenes ya retenidas) se le devuelve.
     */
    retentionsTotal: number;
    adminChargesTotal: number;
    /** Pagos de más que se descuentan al cerrar: solo con la liquidación abierta y si el profesional debe. */
    accountDebt: number | null;
    /**
     * Saldo a favor: lo que la institución le debe por otros comprobantes de la cuenta corriente (rendiciones u otras
     * liquidaciones sin pagar del todo). Se puede pagar desde esta liquidación; null si no hay o ya está paga.
     */
    accountCredit: number | null;
    /** Abierta: proyección del cierre. Cerrada: el neto guardado. */
    netToPay: number;
    totalPaymentOrders: number;
    /** Lo que falta pagar de esta liquidación (pendiente de su comprobante interno); null mientras está abierta. */
    pendingAmount: number | null;
    /** Lo que se le puede pagar desde esta liquidación: lo propio más el saldo a favor. */
    totalOwed: number;
    /** Comprobante interno (el de la liquidación o el de su rendición); null si no tiene. */
    internalReceiptForm: number | null;
    /**
     * Gastos que superan lo que cobra. Abierta: proyección (al cerrar compensan el saldo a favor y lo que queda es
     * deuda). Cerrada: lo que se arrastró. null si no hay o se cerró sin comprobante de gastos.
     */
    adminChargesExcess: number | null;
    /** Lo que falta descontar del excedente de gastos; solo con la liquidación cerrada y si hubo excedente. */
    adminChargesDebtPending: number | null;
    status: string;
    statusId: number;
}

export interface HonorariumLiquidationDetailDTO {
    honorariumLiquidationDetail: number;
    detailType: number;
    sourceType: string;
    amount: number;
    date: string;
    description: string;
    administrativeForm: number | null;
    benefitOrder: number | null;
    honorariumPercentage: number;
    taxBase: number;
    taxRate: number;
    taxType: number | null;
    taxAmount: number;
    taxTypeName?: string | null;
}

export interface HonorariumLiquidationDetailGroupDTO {
    groupLabel: string;
    /** 1 = honorarios, 2 = retenciones. */
    detailType: number;
    totalAmount: number;
    totalTaxAmount: number;
    children: HonorariumLiquidationDetailDTO[];
}

export interface HonorariumLiquidationDetailResponseDTO {
    groups: HonorariumLiquidationDetailGroupDTO[];
    totalHonorarium: number;
    totalWithholdings: number;
    netAmount: number;
}

export interface RemoveLiquidationItemResultDTO {
    /** El profesional se quedó sin ítems de honorario ni retenciones y salió de la liquidación. */
    professionalRemoved: boolean;
}

/**
 * Movimiento de una orden que facturó el profesional como emisor, con lo que se le retiene: el monto menos la parte
 * del profesional según el contrato de distribución.
 */
export interface RetentionLineDTO {
    /** Profesional emisor: a quien se le retiene. */
    issuer: number;
    /** Profesional de la línea (puede no ser el emisor). */
    professional: number;
    professionalName: string | null;
    benefitOrder: number | null;
    date: string | null;
    patientName: string | null;
    nomenclatorDescription: string | null;
    nomenclatorCode: string | null;
    description: string | null;
    movementDetail: string | null;
    /** Monto de toda la línea. */
    amount: number;
    /** Parte del profesional de la línea. */
    professionalAmount: number;
    /** Porcentaje del profesional; null con importe fijo. */
    honorariumPercentage: number | null;
    /** Lo que se le retiene; negativo si se le devuelve (anulación). */
    retentionAmount: number;
    /** Porcentaje del monto que se retiene; null sin monto. */
    retainedPercentage: number | null;
    /** Título: "Descuento orden N° 26850 · paciente · código · 5 %". Lo arma el backend. */
    label: string;
    movementType: number | null;
}

/** Deuda de cuenta corriente que se descuenta, con su origen ("Pago de más · OP N° 7"…). */
export interface AccountDebtLineDTO {
    date: string | null;
    label: string;
    /** Lo que se descuenta de esta deuda. */
    amount: number;
}

/** Retenciones por facturación propia pendientes de un profesional, para elegirlo al crear una liquidación. */
export interface PendingRetentionSummaryDTO {
    professional: number;
    professionalName: string;
    lineCount: number;
    retentionTotal: number;
}

/** Totales de la liquidación: la suma de los montos de cada profesional, calculada por el backend. */
export interface HonorariumLiquidationProfessionalsTotalsDTO {
    totalHonorarium: number;
    totalWithholdings: number;
    totalAdvances: number;
    totalAdminCharges: number;
    totalManualDebits: number;
    totalManualCredits: number;
    /** Retenciones por facturación propia. */
    totalRetentions: number;
    totalAccountDebt: number;
    totalAccountDeduction: number;
    /** Anticipos + deducciones de cuenta corriente + débitos manuales. */
    totalDeductions: number;
    totalNet: number;
    totalPaymentOrders: number;
    totalPending: number;
    /** Saldo a favor de los profesionales (otros comprobantes pendientes de sus cuentas). */
    totalAccountCredit: number;
    /** Gastos que superan lo que cobra cada profesional. */
    totalAdminChargesExcess: number;
}

/** Comprobante pendiente de la cuenta corriente que forma parte del saldo a favor del profesional. */
export interface HonorariumLiquidationAccountCreditDTO {
    administrativeForm: number;
    /** "Rendición N° X", "Liquidación N° Y" o "Comprobante N° Z". */
    origin: string;
    date: string;
    pendingAmount: number;
}

export interface HonorariumAdministrativeChargeDTO {
    honorariumLiquidationDetail: number;
    amount: number;
    date: string;
    description: string;
    administrativeForm: number | null;
}

export interface AddAdministrativeChargeRequestDTO {
    liquidationProfessionalId: number;
    amount: number;
    description: string;
}

/** Débito: lo que el profesional le debe a la institución (resta). Crédito: lo que se le reconoce (suma). */
export type ManualAdjustmentType = 'DEBIT' | 'CREDIT';

/** Débito o crédito manual con descripción y monto (siempre positivo: el tipo dice si resta o suma). */
export interface ManualAdjustmentDTO {
    type: ManualAdjustmentType;
    amount: number;
    description: string;
}

export interface AddManualAdjustmentRequestDTO extends ManualAdjustmentDTO {
    liquidationProfessionalId: number;
}

export interface HonorariumManualAdjustmentDTO {
    honorariumLiquidationDetail: number;
    type: ManualAdjustmentType;
    amount: number;
    date: string;
    description: string;
    /** Comprobante en el que quedó al cerrar (el de gastos, para los débitos). */
    administrativeForm: number | null;
}

/** Errores de negocio de la liquidación que la UI trata de forma puntual. */
export const LIQUIDATION_ERROR_CODES = {
    PAYMENT_ORDER_EXCEEDS_PENDING: 'PAYMENT_ORDER_EXCEEDS_PENDING',
    LIQUIDATION_INVALID_PERIOD: 'LIQUIDATION_INVALID_PERIOD',
    PRELIQUIDATION_ITEMS_UNAVAILABLE: 'PRELIQUIDATION_ITEMS_UNAVAILABLE',
} as const;
